import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  Plus, 
  Edit, 
  Trash2, 
  Upload, 
  Download,
  Search,
  Building2,
  Eye
} from 'lucide-react'
import { motion } from 'framer-motion'
import { portfolioTrackerService } from '@/services/portfolioTrackerService'
import { 
  PortfolioTrackerSummary
} from '@/types/portfolio'
import { formatCurrency } from '@/utils'
import { parseNumberWithSuffix } from '@/utils/money'
import { PageTemplate } from '@/components/templates'
import { Button, Input } from '@/components/atoms'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { CONTAINER_SIZES } from '@/constants'
import { projectService } from '@/services/projectService'
import { ProjectSearchResult } from '@/types/project'

const PortfolioAssetsScreen = () => {
  const { portfolioId } = useParams<{ portfolioId: string }>()
  const navigate = useNavigate()
  
  const [portfolio, setPortfolio] = useState<PortfolioTrackerSummary | null>(null)
  const [assets, setAssets] = useState<PortfolioTrackerSummary['assets']>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [selectedAsset, setSelectedAsset] = useState<PortfolioTrackerSummary['assets'][0] | null>(null)
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  
  // Project search states
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<ProjectSearchResult[]>([])
  const [showSearchResults, setShowSearchResults] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)
  const [selectedProject, setSelectedProject] = useState<ProjectSearchResult | null>(null)

  // Form states
  const [formData, setFormData] = useState({
    project_name: '',
    project_website: '',
    date: '',
    invested_amount: '',
    valuation: '',
    equity_or_tokens_amount: '',
    terms: ''
  })

  useEffect(() => {
    if (portfolioId) {
      loadData()
    }
  }, [portfolioId])

  const loadData = async () => {
    if (!portfolioId) return

    try {
      setLoading(true)
      setError(null)
      
      const portfolioData = await portfolioTrackerService.getPortfolioSummary(portfolioId)
      
      console.log('Portfolio data:', portfolioData)
      console.log('Assets from portfolio summary:', portfolioData.assets)
      console.log('First asset project info:', portfolioData.assets?.[0]?.project)
      console.log('Assets count:', portfolioData.assets?.length || 0)
      console.log('First asset structure:', portfolioData.assets?.[0] ? Object.keys(portfolioData.assets[0]) : 'No assets')
      
      setPortfolio(portfolioData)
      setAssets(portfolioData.assets || [])
    } catch (err) {
      setError('Failed to load portfolio data')
      console.error('Error loading portfolio data:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleAddAsset = async () => {
    if (!portfolioId) return

    try {
      // Parse amounts with suffix support (k, m, b)
      const investedAmount = typeof formData.invested_amount === 'string' 
        ? (parseNumberWithSuffix(formData.invested_amount) || parseFloat(formData.invested_amount) || 0)
        : formData.invested_amount || 0
      const valuation = typeof formData.valuation === 'string'
        ? (parseNumberWithSuffix(formData.valuation) || parseFloat(formData.valuation) || 0)
        : formData.valuation || 0
      const equityAmount = typeof formData.equity_or_tokens_amount === 'string'
        ? (parseNumberWithSuffix(formData.equity_or_tokens_amount) || parseFloat(formData.equity_or_tokens_amount) || 0)
        : formData.equity_or_tokens_amount || 0

      const assetData = {
        project_name: formData.project_name.trim(),
        project_website: formData.project_website.trim() || undefined,
        date: formData.date,
        invested_amount: investedAmount,
        valuation: valuation,
        equity_or_tokens_amount: equityAmount,
        terms: formData.terms.trim()
      }

      await portfolioTrackerService.createAsset(portfolioId, assetData)
      setShowAddModal(false)
      resetForm()
      setSuccessMessage('Asset added successfully!')
      
      // Reload portfolio summary to get updated data with logos
      const portfolioData = await portfolioTrackerService.getPortfolioSummary(portfolioId)
      setPortfolio(portfolioData)
      setAssets(portfolioData.assets || [])
      
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      setError('Failed to add asset')
      console.error('Error adding asset:', err)
    }
  }

  const handleEditAsset = async () => {
    if (!selectedAsset) return

    try {
      // Parse amounts with suffix support (k, m, b)
      const investedAmount = typeof formData.invested_amount === 'string' 
        ? (parseNumberWithSuffix(formData.invested_amount) || parseFloat(formData.invested_amount) || 0)
        : formData.invested_amount || 0
      const valuation = typeof formData.valuation === 'string'
        ? (parseNumberWithSuffix(formData.valuation) || parseFloat(formData.valuation) || 0)
        : formData.valuation || 0
      const equityAmount = typeof formData.equity_or_tokens_amount === 'string'
        ? (parseNumberWithSuffix(formData.equity_or_tokens_amount) || parseFloat(formData.equity_or_tokens_amount) || 0)
        : formData.equity_or_tokens_amount || 0

      const assetData = {
        project_name: formData.project_name.trim(),
        project_website: formData.project_website.trim() || undefined,
        date: formData.date,
        invested_amount: investedAmount,
        valuation: valuation,
        equity_or_tokens_amount: equityAmount,
        terms: formData.terms.trim()
      }

      await portfolioTrackerService.updateAsset(selectedAsset.asset_id, assetData)
      setShowEditModal(false)
      setSelectedAsset(null)
      resetForm()
      setSuccessMessage('Asset updated successfully!')
      
      // Reload portfolio summary to get updated data with logos
      if (portfolioId) {
        const portfolioData = await portfolioTrackerService.getPortfolioSummary(portfolioId)
        setPortfolio(portfolioData)
        setAssets(portfolioData.assets || [])
      }
      
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      setError('Failed to update asset')
      console.error('Error updating asset:', err)
    }
  }

  const handleDeleteAsset = async (assetId: string, projectName: string) => {
    if (!confirm(`Are you sure you want to delete "${projectName}"? This action cannot be undone.`)) {
      return
    }

    try {
      await portfolioTrackerService.deleteAsset(assetId)
      setSuccessMessage('Asset deleted successfully!')
      
      // Reload portfolio summary to get updated data with logos
      if (portfolioId) {
        const portfolioData = await portfolioTrackerService.getPortfolioSummary(portfolioId)
        setPortfolio(portfolioData)
        setAssets(portfolioData.assets || [])
      }
      
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      setError('Failed to delete asset')
      console.error('Error deleting asset:', err)
    }
  }

  const handleImportCSV = async () => {
    if (!portfolioId || !csvFile) return

    try {
      const result = await portfolioTrackerService.addAssetsFromCSV(portfolioId, csvFile)
      setCsvFile(null)
      setShowImportModal(false)
      setSuccessMessage(`Successfully imported ${result.matched_assets} assets!`)
      
      // Reload portfolio summary to get updated data with logos
      const portfolioData = await portfolioTrackerService.getPortfolioSummary(portfolioId)
      setPortfolio(portfolioData)
      setAssets(portfolioData.assets || [])
      
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err: any) {
      if (portfolioTrackerService.isCSVError(err)) {
        setError(`CSV Error: ${err.error}`)
      } else {
        setError('Failed to import CSV')
      }
      console.error('Error importing CSV:', err)
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file && file.type === 'text/csv') {
      setCsvFile(file)
      setError(null)
    } else {
      setError('Please select a valid CSV file')
      setCsvFile(null)
    }
  }



  const resetForm = () => {
    setFormData({
      project_name: '',
      project_website: '',
      date: '',
      invested_amount: '',
      valuation: '',
      equity_or_tokens_amount: '',
      terms: ''
    })
    setSearchQuery('')
    setSelectedProject(null)
    setShowSearchResults(false)
  }

  // Search for projects
  const searchProjects = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([])
      setShowSearchResults(false)
      return
    }
    
    try {
      setSearchLoading(true) 
      const results = await projectService.searchProjects(query)
      console.log('Search results:', results) // Debug log
      setSearchResults(results)
      setShowSearchResults(true)
    } catch (err) {
      console.error('Error searching projects:', err)
    } finally {
      setSearchLoading(false)
    }
  }

  // Handle project selection
  const handleSelectProject = (project: ProjectSearchResult) => {
    setSelectedProject(project)
    const projectName = project.name || ''
    setFormData(prev => ({
      ...prev,
      project_name: projectName,
      project_website: ''
    }))
    setSearchQuery(projectName)
    setShowSearchResults(false)
  }

  // Handle clearing selected project
  const handleClearProject = () => {
    setSelectedProject(null)
    setFormData(prev => ({
      ...prev,
      project_name: ''
    }))
    setSearchQuery('')
    setShowSearchResults(false)
  }

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== formData.project_name) {
        searchProjects(searchQuery)
      }
    }, 300)
    
    return () => clearTimeout(timer)
  }, [searchQuery, formData.project_name])

  const downloadCSVTemplate = () => {
    const template = `Project Name,Date of Investment,Invested Amount,Project Website,Project Valuation,Amount of equity or tokens,Terms
Example Project,2023-01-15,50000,https://example.com,1000000,100000,SAFE agreement`
    
    const blob = new Blob([template], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'portfolio_assets_template.csv'
    a.click()
    window.URL.revokeObjectURL(url)
  }

  const filteredAssets = assets.filter(asset =>
    asset.project?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (loading) {
    return <PageTemplate isLoading />
  }

  if (error && !portfolio) {
    return (
      <PageTemplate
        title="Portfolio Not Found"
        subtitle="The requested portfolio could not be loaded"
        maxWidth={CONTAINER_SIZES.WIDE}
      >
        <Card>
          <CardContent className="p-12 text-center">
            <Building2 className="mx-auto text-muted-foreground mb-4" size={48} />
            <h3 className="text-lg font-semibold mb-2">Portfolio Not Found</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button
              variant="primary"
              leftIcon={<ArrowLeft size={16} />}
              onClick={() => navigate('/portfolio')}
            >
              Back to Portfolios
            </Button>
          </CardContent>
        </Card>
      </PageTemplate>
    )
  }

  return (
    <PageTemplate
      title="Portfolio Assets"
      subtitle="Manage your portfolio assets"
      maxWidth={CONTAINER_SIZES.WIDE}
    >
      {/* Success Message */}
      {successMessage && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 rounded-lg mb-6"
        >
          {successMessage}
        </motion.div>
      )}

      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg mb-6"
        >
          {error}
        </motion.div>
      )}

      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <Button
          variant="outline"
          leftIcon={<ArrowLeft size={16} />}
          onClick={() => navigate(`/portfolio/${portfolioId}`)}
        >
          Back to Portfolio
        </Button>
        
        <Button
          variant="primary"
          leftIcon={<Plus size={16} />}
          onClick={() => setShowAddModal(true)}
        >
          Add Asset
        </Button>
        
        <Button
          variant="secondary"
          leftIcon={<Upload size={16} />}
          onClick={() => setShowImportModal(true)}
        >
          Import CSV
        </Button>
        
        <Button
          variant="outline"
          leftIcon={<Download size={16} />}
          onClick={downloadCSVTemplate}
        >
          Download Template
        </Button>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={16} />
            <Input
              placeholder="Search assets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      </div>

      {/* Portfolio Assets List */}
      <PageTemplate.Section title="Portfolio Assets">
        <div className="mt-8">
          {!assets || assets.length === 0 ? (
            <Card variant="elevated">
              <CardContent className="p-12 text-center">
                <Building2 className="mx-auto text-muted-foreground mb-4" size={48} />
                <h3 className="text-lg font-semibold mb-2">No assets yet</h3>
                <p className="text-muted-foreground mb-4">
                  Add assets to your portfolio to start tracking performance
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    variant="primary"
                    leftIcon={<Plus size={16} />}
                    onClick={() => setShowAddModal(true)}
                  >
                    Add Asset
                  </Button>
                  <Button
                    variant="secondary"
                    leftIcon={<Upload size={16} />}
                    onClick={() => setShowImportModal(true)}
                  >
                    Import CSV
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card variant="elevated">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Portfolio Assets ({filteredAssets.length})</CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      leftIcon={<Plus size={16} />}
                      onClick={() => setShowAddModal(true)}
                    >
                      Add Asset
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<Upload size={16} />}
                      onClick={() => setShowImportModal(true)}
                    >
                      Import CSV
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {filteredAssets.map((asset) => {
                    console.log('Rendering asset:', {
                      asset_id: asset.asset_id,
                      project_name: asset.project?.name,
                      project: asset.project,
                      has_logo: !!asset.project?.logo,
                      logo_url: asset.project?.logo,
                      project_structure: JSON.stringify(asset.project, null, 2)
                    })
                    return (
                    <Card key={asset.asset_id} variant="outlined" className="hover:shadow-sm transition-shadow">
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              {asset.project?.logo ? (
                                <>
                                  <img 
                                    src={asset.project.logo} 
                                    alt={asset.project?.name || 'Project'}
                                    className="w-10 h-10 rounded-lg object-cover"
                                    onError={(e) => {
                                      console.log('Failed to load asset logo:', asset.project?.logo)
                                      e.currentTarget.style.display = 'none'
                                      e.currentTarget.nextElementSibling?.classList.remove('hidden')
                                    }}
                                    onLoad={() => {
                                      console.log('Successfully loaded asset logo:', asset.project?.logo)
                                    }}
                                  />
                                  <div className="hidden w-10 h-10 bg-primary-orange/10 rounded-lg flex items-center justify-center absolute top-0 left-0">
                                    <Building2 className="text-primary-orange" size={20} />
                                  </div>
                                </>
                              ) : (
                                <div className="w-10 h-10 bg-primary-orange/10 rounded-lg flex items-center justify-center">
                                  <Building2 className="text-primary-orange" size={20} />
                                </div>
                              )}
                            </div>
                            <div>
                              <h3 className="text-base font-semibold">{asset.project?.name || 'Unknown Project'}</h3>
                              <p className="text-sm text-muted-foreground">
                              Invested: {formatCurrency(asset.invested_amount || 0)}
                            </p>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <p className="text-lg font-semibold">
                            {formatCurrency(asset.current_value || 0)}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Gain/Loss: {formatCurrency(asset.gain_loss_usd || 0)} ({asset.gain_loss_percentage?.toFixed(2) || 0}%)
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/portfolio/${portfolioId}/asset/${asset.asset_id}`)}
                          >
                            <Eye size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedAsset(asset)
                              setFormData({
                                project_name: asset.project?.name || '',
                                project_website: '',
                                date: '',
                                invested_amount: asset.invested_amount?.toString() || '',
                                valuation: asset.current_value?.toString() || '',
                                equity_or_tokens_amount: '',
                                terms: ''
                              })
                              setShowEditModal(true)
                            }}
                          >
                            <Edit size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteAsset(asset.asset_id, asset.project?.name || 'Unknown Project')}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )})}
              </div>
            </CardContent>
          </Card>
        )}
        </div>
      </PageTemplate.Section>

      {/* Add Asset Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Add New Asset</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Project Name *</label>
                  <div className="relative">
                    {selectedProject ? (
                      <div className="flex items-center gap-2 p-2 border border-input rounded-md bg-muted">
                        {selectedProject.logo && (
                          <img 
                            src={selectedProject.logo} 
                            alt=""
                            className="w-5 h-5 rounded"
                          />
                        )}
                        <span className="flex-1">{selectedProject.name}</span>
                        <button 
                          type="button"
                          className="text-muted-foreground hover:text-foreground"
                          onClick={handleClearProject}
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <>
                        <Input
                          value={searchQuery}
                          onChange={(e) => {
                            setSearchQuery(e.target.value)
                            setFormData(prev => ({ ...prev, project_name: e.target.value }))
                          }}
                          placeholder="Search for project or enter name"
                        />
                        {searchLoading && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                          </div>
                        )}
                      </>
                    )}
                    
                    {showSearchResults && searchResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-background border border-input rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {searchResults.map((project) => (
                          <div
                            key={project.project_id || project.name}
                            className="flex items-center gap-2 p-2 hover:bg-muted cursor-pointer"
                            onClick={() => handleSelectProject(project)}
                          >
                            {project.logo ? (
                              <img 
                                src={project.logo} 
                                alt=""
                                className="w-5 h-5 rounded object-cover"
                                onError={(e) => {
                                  console.log('Failed to load logo:', project.logo)
                                  e.currentTarget.style.display = 'none'
                                }}
                                onLoad={() => {
                                  console.log('Successfully loaded logo:', project.logo)
                                }}
                              />
                            ) : (
                              <div className="w-5 h-5 rounded bg-muted flex items-center justify-center">
                                <Building2 size={12} className="text-muted-foreground" />
                              </div>
                            )}
                            <span>{project.name}</span>
                            {!project.project_id && (
                              <Badge variant="secondary" className="ml-auto text-xs">
                                Unpaired
                              </Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Project Website</label>
                  <Input
                    value={formData.project_website}
                    onChange={(e) => setFormData(prev => ({ ...prev, project_website: e.target.value }))}
                    placeholder="https://example.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Investment Date *</label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Invested Amount (USD) *</label>
                  <Input
                    type="text"
                    value={formData.invested_amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, invested_amount: e.target.value }))}
                    placeholder="50000 or 50k, 1.5M, 2B"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Supports formats: 1000, 50k, 1.5M, 2B
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Project Valuation (USD)</label>
                  <Input
                    type="text"
                    value={formData.valuation}
                    onChange={(e) => setFormData(prev => ({ ...prev, valuation: e.target.value }))}
                    placeholder="1000000 or 1M, 5B"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Supports formats: 1000000, 1M, 5B
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Equity/Token Amount</label>
                  <Input
                    type="text"
                    value={formData.equity_or_tokens_amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, equity_or_tokens_amount: e.target.value }))}
                    placeholder="100000 or 100k, 0.05 (5%)"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Supports formats: 100000, 100k, 0.05 for 5%
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Terms *</label>
                  <Input
                    value={formData.terms}
                    onChange={(e) => setFormData(prev => ({ ...prev, terms: e.target.value }))}
                    placeholder="SAFE agreement, equity, tokens, etc."
                  />
                </div>
                
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowAddModal(false)
                      resetForm()
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleAddAsset}
                    disabled={!formData.project_name || !formData.date || !formData.invested_amount || !formData.terms}
                    className="flex-1"
                  >
                    Add Asset
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Asset Modal */}
      {showEditModal && selectedAsset && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Edit Asset</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Project Name *</label>
                <Input
                  value={formData.project_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, project_name: e.target.value }))}
                  placeholder="Enter project name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Project Website</label>
                <Input
                  value={formData.project_website}
                  onChange={(e) => setFormData(prev => ({ ...prev, project_website: e.target.value }))}
                  placeholder="https://example.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Investment Date *</label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
              
                            <div>
                <label className="block text-sm font-medium mb-2">Invested Amount (USD) *</label>
                <Input
                  type="text"
                  value={formData.invested_amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, invested_amount: e.target.value }))}
                  placeholder="50000 or 50k, 1.5M, 2B"
                />
              </div>
              
                            <div>
                <label className="block text-sm font-medium mb-2">Project Valuation (USD)</label>
                <Input
                  type="text"
                  value={formData.valuation}
                  onChange={(e) => setFormData(prev => ({ ...prev, valuation: e.target.value }))}
                  placeholder="1000000 or 1M, 5B"
                />
              </div>
              
                            <div>
                <label className="block text-sm font-medium mb-2">Equity/Token Amount</label>
                <Input
                  type="text"
                  value={formData.equity_or_tokens_amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, equity_or_tokens_amount: e.target.value }))}
                  placeholder="100000 or 100k, 0.05 (5%)"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Terms *</label>
                <Input
                  value={formData.terms}
                  onChange={(e) => setFormData(prev => ({ ...prev, terms: e.target.value }))}
                  placeholder="SAFE agreement, equity, tokens, etc."
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowEditModal(false)
                    setSelectedAsset(null)
                    resetForm()
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                                  <Button
                    variant="primary"
                    onClick={handleEditAsset}
                    disabled={!formData.project_name || !formData.date || !formData.invested_amount || !formData.terms}
                    className="flex-1"
                  >
                    Save Changes
                  </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Import CSV Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Import Assets from CSV</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">CSV File</label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="w-full p-2 border border-input rounded-md"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Required columns: Project Name, Date of Investment, Invested Amount, Project Valuation, Amount of equity or tokens, Terms
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowImportModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleImportCSV}
                  disabled={!csvFile}
                  className="flex-1"
                >
                  Import
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </PageTemplate>
  )
}

export default PortfolioAssetsScreen
